import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { withKey } from "@/components/invoice-editor/parts";

const field = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500";

const ItemRow = ({ it, idx, onChange, onRemove }) => (
  <div className="grid grid-cols-12 gap-2" data-testid={`tpl-item-row-${idx}`}>
    <input className={`${field} col-span-12 sm:col-span-7`} value={it.description} onChange={(e) => onChange("description", e.target.value)} placeholder="Beschreibung" data-testid={`tpl-item-desc-${idx}`} />
    <input className={`${field} col-span-4 sm:col-span-2`} type="number" step="any" value={it.quantity} onChange={(e) => onChange("quantity", e.target.value)} placeholder="Menge" data-testid={`tpl-item-qty-${idx}`} />
    <input className={`${field} col-span-6 sm:col-span-2`} type="number" step="any" value={it.unit_price} onChange={(e) => onChange("unit_price", e.target.value)} placeholder="Preis €" data-testid={`tpl-item-price-${idx}`} />
    <button type="button" onClick={onRemove} className="col-span-2 sm:col-span-1 h-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`tpl-item-remove-${idx}`}>
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export const TemplateEditDialog = ({ tpl, onClose }) => {
  const qc = useQueryClient();
  const [name, setName] = useState(tpl.name);
  const [note, setNote] = useState(tpl.note || "");
  const [items, setItems] = useState(() => tpl.items.map(withKey));
  const [busy, setBusy] = useState(false);

  const upd = (key, k, v) => setItems(items.map((it) => (it._k === key ? { ...it, [k]: v } : it)));

  const save = async () => {
    if (!name.trim()) return toast.error("Bitte einen Namen angeben.");
    const clean = items.filter((i) => i.description.trim());
    if (!clean.length) return toast.error("Bitte mindestens eine Position angeben.");
    setBusy(true);
    try {
      await api.put(`/templates/${tpl.id}`, {
        name: name.trim(),
        note,
        vat_rate: tpl.vat_rate,
        kleinunternehmer: tpl.kleinunternehmer,
        reference_label: tpl.reference_label || "",
        items: clean.map((i) => ({ description: i.description, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
      });
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Vorlage gespeichert");
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="template-edit-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg">Vorlage bearbeiten</h3>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100" data-testid="close-template-edit"><X className="h-4 w-4" /></button>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1">Name</span>
          <input className={field} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="tpl-edit-name" />
        </label>
        <div className="space-y-2">
          <span className="block text-xs font-medium text-slate-600">Positionen (Menge leer lassen = beim Erstellen ausfüllen)</span>
          {items.map((it, idx) => (
            <ItemRow key={it._k} it={it} idx={idx} onChange={(k, v) => upd(it._k, k, v)} onRemove={() => setItems(items.filter((x) => x._k !== it._k))} />
          ))}
          <button type="button" onClick={() => setItems([...items, withKey({ description: "", quantity: 1, unit_price: 0 })])} data-testid="tpl-add-item" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 border border-dashed border-slate-300 hover:border-slate-400 rounded-lg px-3.5 py-2">
            <Plus className="h-4 w-4" /> Position hinzufügen
          </button>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1">Rechnungstext</span>
          <textarea className={field} rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="tpl-edit-note" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="tpl-edit-save" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg text-sm disabled:opacity-60">
            {busy ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};
